// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@hardwoods/App/Components/Common/XngZone";
import ProductDetailsCategoryPopup from "@hardwoods/App/Components/ProductDetailsCategoryPopup/ProductDetailsCategoryPopup";
import XngSticky from "@hardwoods/App/Components/XngSticky";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getLineNotes, getProductToggleTableMode } from "@hardwoods/App/Store/Selectors/CustomProductDetails";
import { color } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    permissions: state.context.permissions,
    lineNotes: getLineNotes(state),
    tableMode: getProductToggleTableMode(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasShellContext & HasProductContext;

export interface ProductDetailsViewStyles {
    container?: InjectableCss;
    leftSection?: InjectableCss;
    detailsAndSpecificationsSection?: InjectableCss;
    mainContent?: InjectableCss;
    imagesSection?: InjectableCss;
    detailsSection?: InjectableCss;
    detailsSectionActions?: InjectableCss;
    rightSection?: InjectableCss;
    actionsContainer?: InjectableCss;
    pricingContainer?: InjectableCss;
    productDetailsAddToCart?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 80px;
            margin-bottom: 60px;
            &:last-child {
                border: none;
                padding-bottom: 0;
                margin-bottom: 0;
            }
            & [class*="ZoneStyle"] {
                height: auto;
            }
        `,
    },
    leftSection: {
        css: css`
            flex: 0 1 calc(100% - 365px);
            min-width: 525px;
            overflow-wrap: break-word;
            word-wrap: break-word;
            margin-right: 65px;
            margin-bottom: 15px;

            ${xScreen.lte("md")} {
                flex-grow: 1;
                margin-right: 0;
            }
            ${xScreen.lte("sm")} {
                min-width: 0;
                flex: 1 1 100%;
            }
        `,
    },
    mainContent: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    imagesSection: {
        css: css`
            display: flex;
            flex: 0 1 287px;
            position: relative;
            margin-right: 30px;
            margin-bottom: 15px;
            ${xScreen.lte("sm")} {
                flex-basis: 100%;
                margin-right: 0;
            }
        `,
    },
    detailsSection: {
        css: css`
            flex: 1 1 300px;
            margin-bottom: 15px;
        `,
    },
    detailsSectionActions: {
        css: css`
            display: flex;
            align-items: center;
            & > div {
                text-align: left;
                margin-right: 15px;
                width: auto;
                height: 100%;
            }
        `,
    },
    rightSection: {
        css: css`
            flex: 0 1 300px;
            top: 100px;
            margin-bottom: 15px;
            ${xScreen.lte("xs")} {
                flex-grow: 1;
            }
        `,
    },
    detailsAndSpecificationsSection: {
        css: css`
            flex: 0 1 calc(100% - 365px);
            min-width: 525px;
            overflow-wrap: break-word;
            word-wrap: break-word;
            margin-top: 15px;
            ${xScreen.lte("md")} {
                flex-grow: 1;
            }
            ${xScreen.lte("sm")} {
                min-width: 0;
                flex: 1 1 100%;
            }
        `,
    },
    actionsContainer: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-radius: 4px;
            padding: 30px 15px;
        `,
    },
    pricingContainer: {
        css: css`
            & > div {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                & > .productPriceWrapper {
                    width: auto;
                    flex: 1 1 100%;
                    margin-right: 10px;
                    margin-bottom: 5px;
                    &:last-of-type {
                        flex-basis: auto;
                    }
                }
                & > .productAvailabilityContainer {
                    width: auto;
                    margin: 0 0 5px 0;
                    flex: initial;
                }
            }
        `,
    },
    productDetailsAddToCart: {
        css: css`
            & > div {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                border-top: 1px solid ${color("common.border")};
                margin-top: 15px;
                padding-top: 25px;
                &:empty {
                    display: none;
                }
            }
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<Props> = props => {
    return (
        <>
            <StyledWrapper {...styles.container}>
                <StyledWrapper 
                    css={css`
                        ${styles.leftSection?.css}
                        flex: 0 1 calc(100% - ${!props.tableMode ? "365px" : "0px"});
                    `}
                >
                    <StyledWrapper {...styles.mainContent}>
                        <StyledWrapper {...styles.imagesSection}>
                            <StyledWrapper>
                                <XngZone contentId={props.id} zoneName="ImageCarousel" hint="Image Carousel Zone:" />
                            </StyledWrapper>
                            <XngZone contentId={props.id} zoneName="PrimaryImage" hint="Primary Image Zone:" />
                            <ProductDetailsCategoryPopup />
                        </StyledWrapper>
                        <StyledWrapper {...styles.detailsSection}>
                            <XngZone contentId={props.id} zoneName="ProductDetails" hint="Product Details Zone:" />
                            {(props.shellContext.isEditing || props.tableMode) && (
                                <XngZone contentId={props.id} zoneName="ProductTable" hint="Product Table Zone:" />
                            )}
                            <StyledWrapper {...styles.detailsSectionActions}>
                                <XngZone contentId={props.id} zoneName="ProductTableToggle" hint="Product Table Toggle Zone:" />
                                <XngZone contentId={props.id} zoneName="ProductShareLink" hint="Product Share Link Zone:" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                {(props.shellContext.isEditing || !props.tableMode) && (
                    <XngSticky {...styles.rightSection}>
                        <StyledWrapper {...styles.actionsContainer}>
                            <StyledWrapper>
                                <XngZone contentId={props.id} zoneName="QuantityBreakPrice" hint="Quantity Break Price Zone:" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.pricingContainer} data-test-slector="ProductActions">
                                <XngZone contentId={props.id} zoneName="ProductActions" hint="Product Actions Zone:" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.productDetailsAddToCart}>
                                <XngZone contentId={props.id} zoneName="AddToCartSection" hint="Add To Cart Section Zone:" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </XngSticky>
                )}
                <StyledWrapper
                    css={css`
                        ${styles.detailsAndSpecificationsSection?.css}
                        flex: 0 1 calc(100% - ${!props.tableMode ? "365px" : "0px"});
                    `}
                >
                    <XngZone contentId={props.id} zoneName="DetailsAndSpecifications" hint="Details And Specifications Zone:" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withProductContext(connect(mapStateToProps)(ProductDetailsView))),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
